//MyProfile/VisitorPostingsPresentation.tsx

import React, { useEffect, useState } from 'react';
import { HLSPlayer } from '../Gallery/GalleryPresentation';
import { useMediaViewerOverlay } from '../Presentation/MediaViewerOverlay';
import type { Post } from './VisitorPostings';

// ---------------- Types ----------------
interface Props {
  post: Post;
  startIndex: number;
  onClose: () => void;
}
type MediaEntry = Post['media'][number];

const isVideo = (m: MediaEntry): m is MediaEntry & {
  kind: 'video';
  isHls?: boolean;
  videoId?: string;
  hlsUrl?: string | null;
  posterUrl?: string | null;
} => {
  return m.kind === 'video' && !!(m.hlsUrl || m.videoId);
};

// ---------------- Component ----------------
const VisitorPostingsPresentation: React.FC<Props> = ({
  post,
  startIndex,
  onClose,
}) => {

const API_BASE = process.env.REACT_APP_API_BASE_URL as string;
const API_ORIGIN = API_BASE.replace(/\/api(\/)?$/, '').replace(/\/$/, '');

  // ✅ Shared overlay
  const { Overlay, setMuted } =
  useMediaViewerOverlay({ initialMuted: true, pauseBackgroundOnOpen: true });

  // Ensure overlay starts muted (or sync to a globalMuted if you have one)
  useEffect(() => { setMuted(true); }, [setMuted]);

 
  // Track which videos are landscape
const [isVideoLandscape, setIsVideoLandscape] = useState<Record<number, boolean>>({});

const [currentIndex, setCurrentIndex] = useState(startIndex);

const goPrev = () =>
  setCurrentIndex(i => Math.max(0, i - 1));

const goNext = () =>
  setCurrentIndex(i => Math.min(post.media.length - 1, i + 1));


// Update handlers
const handleImgLoad = (index: number, ev: React.SyntheticEvent<HTMLImageElement>) => {
  const img = ev.currentTarget;
  const w = img.naturalWidth || 0;
  const h = img.naturalHeight || 0;
  const landscape = w >= h;
  setIsLandscape(prev => (prev[index] === landscape ? prev : { ...prev, [index]: landscape }));
};

  // Track which grid images are landscape
const [isLandscape, setIsLandscape] = useState<Record<number, boolean>>({});

const mediaUrlOf = (m: MediaEntry): string => {
  if (m.kind === 'video') {
    if (m.hlsUrl) return m.hlsUrl;
    if (m.url && /^https?:\/\//i.test(m.url)) return m.url;
    if (m.videoId) return `${API_ORIGIN}/hls/playlist/${m.videoId}`;
    return '';
  }

  if (m.kind === 'image' && typeof m.url === 'string') {
    return m.url;
  }

  return '';
};

useEffect(() => {
  const prev = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  return () => {
    document.body.style.overflow = prev;
  };
}, []);

useEffect(() => {
  setCurrentIndex(startIndex);
}, [post._id, startIndex]);


useEffect(() => {
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') goPrev();
    if (e.key === 'ArrowRight') goNext();
  };
  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
}, [post.media.length]);

return (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 100000,
      backgroundColor: "#000",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    }}
  >
  {/* ❌ CLOSE */}
<button
  onClick={onClose}
  style={{
    position: "absolute",
    top: 16,
    right: 20,
    zIndex: 100001,
    background: "rgba(0,0,0,0.6)",
    color: "#666",
    border: "none",
    fontSize: 16,
    cursor: "pointer",
    padding: 8,
    borderRadius: 999,
  }}
>
  ✖
</button>

{/* ← PREV */}
{currentIndex > 0 && (
  <button
    onClick={goPrev}
    style={{
      position: "absolute",
      left: 20,
      top: "50%",
      transform: "translateY(-50%)",
      fontSize: 28,
      background: "none",
      color: "#666",
      border: "none",
      cursor: "pointer",
    }}
  >
    ‹
  </button>
)}

{/* → NEXT */}
{currentIndex < post.media.length - 1 && (
  <button
    onClick={goNext}
    style={{
      position: "absolute",
      right: 20,
      top: "50%",
      transform: "translateY(-50%)",
      fontSize: 28,
      background: "none",
      color: "#666",
      border: "none",
      cursor: "pointer",
    }}
  >
    ›
  </button>
)}

    {/* 🎬 ACTIVE MEDIA */}
    {(() => {
      const m = post.media[currentIndex];
      if (!m) return null;

      const video = isVideo(m);
      const url = mediaUrlOf(m);

      const musicLabel = video
        ? [m.musicTitle, m.musicArtist].filter(Boolean).join(" — ")
        : "";

      // IMPORTANT: use a single source of truth for landscape
      const isLandscapeNow =
        (video ? isVideoLandscape[currentIndex] : isLandscape[currentIndex]) ??
        false;

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* MEDIA BLOCK (same layout logic as GalleryPresentation) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              width: isLandscapeNow ? "92vw" : "auto",
              height: isLandscapeNow ? "auto" : "calc(100vh - 28px)",
              maxWidth: isLandscapeNow ? "92vw" : "min(94vw, 760px)",
              maxHeight: isLandscapeNow ? "80vh" : "calc(100vh - 28px)",
            }}
          >
            {video ? (
              <HLSPlayer
                src={url}
                muted
                active
                // NOTE: pass the same computed value (not undefined)
                isLandscape={isLandscapeNow}
                onOrientation={(land) => {
                  setIsVideoLandscape((prev) =>
                    prev[currentIndex] === land
                      ? prev
                      : { ...prev, [currentIndex]: land }
                  );
                }}
                // NOTE: DO NOT pass width/height 100% here (that’s what caused the “zoomed” feeling)
                style={{
                  width: "auto",
                  height: "auto",
                  maxWidth: isLandscapeNow ? "93vw" : "100vh",
                  maxHeight: isLandscapeNow ? "93vh" : "100vh",
                  objectFit: "contain",
                  borderRadius: 12,
                }}
              />
            ) : (
              <img
                src={url}
                alt="presentation-media"
                onLoad={(e) => handleImgLoad(currentIndex, e)}
                style={{
                  width: "auto",
                  height: "auto",
                  maxWidth: isLandscapeNow ? "93vw" : "97vh",
                  maxHeight: isLandscapeNow ? "93vh" : "97vh",
                  objectFit: "contain",
                  borderRadius: 12,
                }}
              />
            )}
          </div>

          {/* 🎵 MUSIC (same as GalleryPresentation: fixed bottom, not affecting sizing) */}
          {musicLabel && (
            <div
              style={{
                position: "fixed",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
                fontSize: 12,
                color: "#777",
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "0px 5px 3px 5px",
                borderRadius: 999,
                background: "transparent",
                maxWidth: "50vw",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                zIndex: 100002,
              }}
            >
              <span>🎵</span>

              {m.musicId ? (
                <a
                  href={`/musicdetails/${m.musicId}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    color: "#aaa",
                    textDecoration: "none",
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {musicLabel}
                </a>
              ) : (
                <span>{musicLabel}</span>
              )}
            </div>
          )}
        </div>
      );
    })()}

    {Overlay}
  </div>
);
};

export default VisitorPostingsPresentation;
